import {Add, ArrowForward, Download, Edit, LocationOnOutlined, PaymentsOutlined, PhotoCameraOutlined,} from '@mui/icons-material';
import {usePersona} from 'src/permissions/hooks';
import {Box, Button, CircularProgress, Tooltip, Typography} from '@mui/material';
import {FC, useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {chargersApi, locationsApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import {axiosInstance} from 'src/axiosBaseQuery';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {NotifyError, NotifyWarning} from 'src/utils/notification';
import {formatPrice, generateItemCost, getBaseURL, isErrorObject} from 'src/utils/helper';
import {LocationUpdateFormWrapper} from '../LocationUpdateFormWrapper';
import {MapWrapper} from 'src/components/map/MapWrapper';
import {blankPhoto, MapZoomEnum} from 'src/utils/consts';
import {LocationDetailsType} from './LocationDetails.types';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {actionSlotSx, squareActionSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {phoneCardSx} from 'src/components/mobile/mobileDesign.sx';
import {
    badgeSx,
    bannerSx,
    cardSx,
    costActionsSx,
    factEmptySx,
    factLabelSx,
    factSx,
    factValueSx,
    factsSx,
    headerSx,
    mapSx,
    photoSx,
    sectionBadgeSx,
    sectionHeaderSx,
    sectionTitleSx,
    subtitleSx,
    titleSx,
    typePillSx,
} from './LocationIdentity.sx';
import {EditLocationCostForm} from '../../forms/EditLocationCostForm';
import {CreateLocationCostForm} from '../../forms/CreateLocationCostForm';
import {ItemCost} from '../../../types';
import {useCan} from 'src/permissions/hooks';

type AddImageMutationTrigger = ReturnType<typeof locationsApi.useAddLocationImageMutation>[0];

const handleTrigger = async (
    file: File[] | null,
    locationId: number,
    trigger: AddImageMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    warningMessage: string,
    handleBlockSubmit: (status: boolean) => void,
    businessId?: number
) => {
    if (!file) {
        NotifyWarning(warningMessage);
        return;
    }
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        formData.set('businessId', String(businessId));
        await trigger({formData, locationId}).unwrap();
        handleCloseImageModal();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        dispatch(preloaderChangeStatus(false));
        handleBlockSubmit(false);
    }
};

export const LocationDetails: FC<LocationDetailsType> = ({data, canReadDetails = true}) => {
    // Editing a cost item and adding one are separate endpoints with separate permissions;
    // the personas listed here held neither, and so do several the list never mentioned.
    const canUpdateLocationCost = useCan('locationCosts.update');
    const canCreateLocationCost = useCan('locationCosts.create');
    // What the location cost and is still worth. The server leaves it out for anyone without this, so
    // the card asks the same question rather than showing an empty figure.
    const canReadLocationCosts = useCan('locationCosts.read');
    const intl = useIntl();
    const persona = usePersona();
    const navigate = useNavigate();
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const [open, setOpen] = useState<boolean>(false);
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [locationImage, setLocationImage] = useState<File[] | null>(null);

    const warningMessage = intl.formatMessage({id: 'uploadAFile'});
    const canUpdateLocation = useCan('locations.update');
    const canExportLocation = useCan('locations.export');
    // Counted by the charger search rather than read off data.chargePoints: the search is clamped to
    // the reader's reach, so the number is the one they may see - the same set the chargers page lists.
    const canSearchChargers = useCan('chargers.search');
    const showChargerCount = canReadDetails && canSearchChargers;
    const {data: locationChargers} = chargersApi.useGetChargersQuery({
        data: {businessId: data.business.id, locationId: data.id},
        params: {page: 0, size: 1},
    }, {skip: !showChargerCount});
    const dialogTitle = intl.formatMessage({id: 'editLocation'});
    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});
    const [blockSubmit, setBlockSubmit] = useState(false);
    const [showEditCost, setShowEditCost] = useState(false);
    const [showCreateCost, setShowCreateCost] = useState(false);
    const [isExportLoading, setIsExportLoading] = useState(false);

    const currentCurrency = data?.itemCosts?.[0]?.currency;

    const chargersCosts = data.chargePoints.reduce(
        (acc, cur) => [...acc, ...cur.itemCosts],
        [] as ItemCost[]
    );

    const handleShowEditCostModal = (status: boolean) => () => {
        setShowEditCost(status);
    };

    const handleShowCreateCostModal = (status: boolean) => () => {
        setShowCreateCost(status);
    };

    const handleExportResidualValue = useCallback(async () => {
        if (isExportLoading) return;

        setIsExportLoading(true);
        try {
            const response = await axiosInstance.get(
                `${getBaseURL()}/locations/${data.id}/residual-value/export`,
                {
                    params: {businessId: businessId},
                    responseType: 'blob',
                    headers: {
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    },
                }
            );
            const blob = response.data as Blob;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `residual_value_location_${data.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export residual value:', error);
            NotifyError(intl.formatMessage({id: 'exportResidualValueError'}));
        } finally {
            setIsExportLoading(false);
        }
    }, [isExportLoading, data.id, businessId, intl]);

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const dispatch = useAppDispatch();
    const [trigger] = locationsApi.useAddLocationImageMutation();

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        setLocationImage(file);
    };

    const handleSubmit = () => {
        locationImage &&
        handleTrigger(
            locationImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            warningMessage,
            handleBlockSubmit,
            data.business.id
        );
    };

    const address = [
        [data.address?.street, data.address?.houseNumber].filter(Boolean).join(' '),
        data.address?.city,
        data.address?.country,
    ].filter(Boolean).join(', ');

    // The pin says which place it is, as on the overview map - without the link, which here would lead
    // to the page the reader is on. Memoised: a new array every render made the map rebuild its pin.
    const mapMarkers = useMemo(
        () => [{lat: data.latitude, lng: data.longitude, title: data.name, subtitle: address}],
        [data.latitude, data.longitude, data.name, address],
    );

    const defaultPrice = data?.price
        ? `${formatPrice(data.price.defaultPrice)} ${data.price.currency}`
        : null;

    /** The dynamic-pricing link, unchanged: same route, same disabled state, same tooltip. */
    const dynamicPricingButton = (
        <Tooltip
            title={!data?.price ? intl.formatMessage({id: 'setDefaultPriceFirst'}) : ''}
            arrow
            placement="top"
        >
            <span>
                <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForward/>}
                    disabled={!data?.price}
                    onClick={() => {
                        if (data?.price?.id) {
                            navigate(`/app/locations/${data.id}/prices/${data.price.id}`);
                        }
                    }}
                    sx={{whiteSpace: 'nowrap'}}
                >
                    <FormattedMessage id="dynamicPriceConfiguration"/>
                </Button>
            </span>
        </Tooltip>
    );

    const cost = generateItemCost([...data.itemCosts, ...chargersCosts]);
    const {isCompact} = useScreenTier();

    /* The map and the photograph across the top. A location is a place before it is a record, and
       the map used to be a third of one row shared with eleven fields. The same pair on both
       widths - what changes is whether they band the card or get one of their own. */
    const banner = (
        <Box sx={bannerSx}>
            <Box sx={mapSx}>
                <MapWrapper
                    markers={mapMarkers}
                    mapCenter={{lat: data.latitude, lng: data.longitude}}
                    zoom={MapZoomEnum.HIGH}
                />
            </Box>
            <Box sx={photoSx}>
                <img
                    src={data.image?.url ?? blankPhoto}
                    alt={data.name}
                />
            </Box>
        </Box>
    );

    /** What kind of place it is - shown, as before, only to a reader who may read the details. */
    const typePill = canReadDetails && data?.locationType ? (
        <Box component="span" sx={typePillSx}>
            <FormattedMessage id={data.locationType}/>
        </Box>
    ) : null;

    /**
     * The facts this card already states, in the order it states them.
     *
     * Both sets are kept: the full one for the back office and the id/name/address one for a reader
     * without locations.readDetails, so the narrow screen draws the same distinction the wide one
     * does. Nothing is re-derived - `address` and the charger count are the very values printed above.
     */
    const phoneFacts = canReadDetails
        ? [
            {id: 'parkingLots', label: <FormattedMessage id="parkingLots"/>, value: data.parkingLots},
            ...(showChargerCount
                ? [{
                    id: 'numberOfChargers',
                    label: <FormattedMessage id="numberOfChargers"/>,
                    value: locationChargers?.page.totalElements ?? '—',
                }]
                : []),
            {
                id: 'availableAmperes',
                label: <FormattedMessage id="availableAmperes"/>,
                value: data.availableAmperes,
            },
            {
                id: 'reOwner',
                label: <FormattedMessage id="reOwner"/>,
                value: data?.realEstate?.name ?? <FormattedMessage id="notSet"/>,
            },
            {id: 'address', label: <FormattedMessage id="address"/>, value: address},
        ]
        : [
            {id: 'id', label: <FormattedMessage id="id"/>, value: data?.id},
            {id: 'name', label: <FormattedMessage id="name"/>, value: data?.name},
            {id: 'address', label: <FormattedMessage id="address"/>, value: address},
        ];

    return (
        <>
            {isCompact && (
                <>
                    <RecordIdentityCard
                        icon={<LocationOnOutlined/>}
                        title={data?.name}
                        subtitle={address}
                        status={typePill}
                        facts={phoneFacts}
                    />
                    {/* The place itself, in a card of its own: on a phone a map banded across the
                        top of the identity is a picture before it is a location. */}
                    <Box sx={phoneCardSx}>{banner}</Box>
                </>
            )}

            {!isCompact && (
            <Box sx={cardSx}>
                {banner}

                <Box sx={headerSx}>
                    <Box sx={badgeSx}>
                        <LocationOnOutlined/>
                    </Box>
                    <Box sx={{minWidth: 0}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                            <Typography sx={titleSx}>{data?.name}</Typography>
                            {canReadDetails && data?.locationType && (
                                <Box component="span" sx={typePillSx}>
                                    <FormattedMessage id={data.locationType}/>
                                </Box>
                            )}
                        </Box>
                        <Typography sx={subtitleSx}>{address}</Typography>
                    </Box>

                    <Box sx={{flex: '1 1 auto'}}/>

                    {/* Both behind locations.update, as before. Gated on `get` the edit appeared for
                        anybody who could open the page - a driver included - and led to a form whose
                        save the server refuses. */}
                    {canUpdateLocation && (
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<PhotoCameraOutlined/>}
                            onClick={() => setOpenDropZone(true)}
                        >
                            <FormattedMessage id="photo"/>
                        </Button>
                    )}
                    {canUpdateLocation && (
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<Edit/>}
                            onClick={handleOpenModal}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    )}
                </Box>

                {/* A fifth column when the charger count joins the row, so it stays one row. */}
                <Box
                    sx={showChargerCount
                        ? {...factsSx as object, gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)'}}
                        : factsSx}
                >
                    {canReadDetails ? (
                        <>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="parkingLots"/>
                                </Typography>
                                <Typography sx={factValueSx}>{data.parkingLots}</Typography>
                            </Box>
                            {showChargerCount && (
                                <Box sx={factSx}>
                                    <Typography sx={factLabelSx}>
                                        <FormattedMessage id="numberOfChargers"/>
                                    </Typography>
                                    <Typography sx={factValueSx}>
                                        {locationChargers?.page.totalElements ?? '—'}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="availableAmperes"/>
                                </Typography>
                                <Typography sx={factValueSx}>{data.availableAmperes}</Typography>
                            </Box>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="reOwner"/>
                                </Typography>
                                {data?.realEstate?.name
                                    ? <Typography sx={factValueSx}>{data.realEstate.name}</Typography>
                                    : <Typography sx={factEmptySx}><FormattedMessage id="notSet"/></Typography>}
                            </Box>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="address"/>
                                </Typography>
                                <Typography sx={factValueSx}>{address}</Typography>
                            </Box>
                        </>
                    ) : (
                        <>
                            {/* The compact set, unchanged: id, name and address. The reader without
                                locations.readDetails now lands in the same cards instead of in a
                                differently proportioned row. */}
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="id"/>
                                </Typography>
                                <Typography sx={factValueSx}>{data?.id}</Typography>
                            </Box>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="name"/>
                                </Typography>
                                <Typography sx={factValueSx}>{data?.name}</Typography>
                            </Box>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="address"/>
                                </Typography>
                                <Typography sx={factValueSx}>{address}</Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
            )}

            {/* Everything the location charges for, in one card. These four were spread between the
                two sub-columns of the old form with the physical facts in between. */}
            <Box sx={cardSx}>
                <Box sx={sectionHeaderSx}>
                    <Box sx={sectionBadgeSx}>
                        <PaymentsOutlined/>
                    </Box>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="pricing"/>
                    </Typography>
                    <Box sx={{flex: '1 1 auto'}}/>
                    {dynamicPricingButton}
                </Box>

                <Box sx={factsSx}>
                    <Box sx={factSx}>
                        <Typography sx={factLabelSx}>
                            <FormattedMessage id="defaultPrice"/>
                        </Typography>
                        {defaultPrice
                            ? <Typography sx={factValueSx}>{defaultPrice}</Typography>
                            : <Typography sx={factEmptySx}><FormattedMessage id="notSet"/></Typography>}
                    </Box>

                    {canReadDetails && (
                        <>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="guestPricePerKwh"/>
                                </Typography>
                                {data?.guestTariff?.pricePerKwh
                                    ? (
                                        <Typography sx={factValueSx}>
                                            {`${formatPrice(data.guestTariff.pricePerKwh)} ${data.guestTariff.currency || data.price?.currency || ''}`}
                                        </Typography>
                                    )
                                    : <Typography sx={factEmptySx}><FormattedMessage id="notSet"/></Typography>}
                            </Box>
                            <Box sx={factSx}>
                                <Typography sx={factLabelSx}>
                                    <FormattedMessage id="guestHoldAmount"/>
                                </Typography>
                                {data?.guestTariff?.holdAmount
                                    ? (
                                        <Typography sx={factValueSx}>
                                            {`${formatPrice(data.guestTariff.holdAmount)} ${data.guestTariff.currency || data.price?.currency || ''}`}
                                        </Typography>
                                    )
                                    : <Typography sx={factEmptySx}><FormattedMessage id="notSet"/></Typography>}
                            </Box>
                            {canReadLocationCosts && (
                                <Box sx={factSx}>
                                    <Typography sx={factLabelSx}>
                                        <FormattedMessage id="currentCostLocation"/>
                                    </Typography>
                                    <Typography sx={factValueSx}>
                                        <Typography component="span" variant="caption" sx={{fontWeight: 400}}>Initial </Typography>
                                        {cost.initTotal} {cost.currency}
                                        <Typography component="span" variant="caption" sx={{fontWeight: 400}}> / Amortized </Typography>
                                        {cost.amortizedTotal} {cost.currency}
                                    </Typography>
                                    {/* Three unlabelled glyphs in a row, one of them with a tooltip:
                                        editing a cost, adding one and exporting the residual value are
                                        three different things. Same three permissions. */}
                                    <Box sx={costActionsSx}>
                                        {canUpdateLocationCost && !!data?.itemCosts?.length && (
                                            <Button
                                                variant="contained"
                                                sx={actionButtonSx('28px', 'secondary')}
                                                startIcon={<Edit/>}
                                                onClick={handleShowEditCostModal(true)}
                                            >
                                                <FormattedMessage id="edit"/>
                                            </Button>
                                        )}
                                        {canCreateLocationCost && (
                                            <Button
                                                variant="contained"
                                                sx={actionButtonSx('28px', 'secondary')}
                                                startIcon={<Add/>}
                                                onClick={handleShowCreateCostModal(true)}
                                            >
                                                <FormattedMessage id="add"/>
                                            </Button>
                                        )}
                                        {canExportLocation && (
                                            <Tooltip title={intl.formatMessage({id: 'exportResidualValue'})}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        sx={actionButtonSx('28px', 'secondary')}
                                                        disabled={isExportLoading}
                                                        startIcon={isExportLoading ? <CircularProgress size={14}/> : <Download/>}
                                                        onClick={handleExportResidualValue}
                                                    >
                                                        <FormattedMessage id="export"/>
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {isCompact && canUpdateLocation && (
                <RecordActionBar>
                    {/* The two the page already offers, both behind locations.update and both
                        opening the modals they always did: the photograph as the outlined square,
                        editing the location as the primary. */}
                    <Box sx={squareActionSx}>
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<PhotoCameraOutlined/>}
                            onClick={() => setOpenDropZone(true)}
                            aria-label={intl.formatMessage({id: 'photo'})}
                        />
                    </Box>
                    <Box sx={actionSlotSx(true)}>
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<Edit/>}
                            onClick={handleOpenModal}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    </Box>
                </RecordActionBar>
            )}

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={handleShowEditCostModal(false)}
                open={showEditCost}
                title={intl.formatMessage({id: 'currentCostLocation'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <EditLocationCostForm
                    onClose={handleShowEditCostModal(false)}
                    data={data?.itemCosts ?? []}
                    chargersCosts={chargersCosts}
                    locationId={data.id}
                    businessId={businessId}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleShowCreateCostModal(false)}
                open={showCreateCost}
                title={intl.formatMessage({id: 'currentCostLocation'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <CreateLocationCostForm
                    currentCurrency={currentCurrency}
                    onClose={handleShowCreateCostModal(false)}
                    locationId={data.id}
                    businessId={businessId}
                />
            </AMPModal>
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <LocationUpdateFormWrapper
                    handleClose={handleCloseModal}
                    authInfo={authInfo}
                    initialValue={{...data, realEstateId: data?.realEstate?.id ?? null}}
                    locationId={data.id}
                />
            </AMPModal>
        </>
    );
};
